/**
 * Braze Web SDK - In-App Message Popups
 * GDPR: Only initializes when marketing consent is given
 *
 * Initialization order (per Braze docs):
 *   1. braze.initialize() — configure SDK (once)
 *   2. braze.automaticallyShowInAppMessages() — register IAM handler (once)
 *   3. changeUser() or addAlias() — identify the user
 *   4. braze.openSession() — starts session, fetches trigger configs
 *   5. braze.logCustomEvent() — fires immediately; SDK queues internally
 *      and replays against trigger configs when they arrive (v2.3.1+)
 *   6. braze.requestImmediateDataFlush() — push everything to Braze
 *
 * Turbolinks: This app uses Turbolinks, so navigations are AJAX body
 * swaps — DOMContentLoaded only fires on full page loads. We listen
 * for turbolinks:load instead, which fires on BOTH initial load and
 * every Turbolinks navigation.
 *
 * sessionTimeoutInSeconds: 1 ensures every page navigation starts a
 * NEW session, so trigger configs are always fresh.
 *
 * NOTE: Uses a Braze Campaign (not Canvas) for in-app messages.
 */
import * as braze from '@braze/web-sdk';

let brazeSDKInitialized = false;
let brazeAliasSet = false;

function generateAliasName() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return 'web_' + crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'web_' + 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0;
    var v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function startBrazeSession() {
  braze.openSession();
  braze.logCustomEvent('web_page_ready', {
    page_path: window.location.pathname,
    page_url: window.location.href
  });
  // console.log('[Braze] openSession + web_page_ready fired for', window.location.pathname);
  braze.requestImmediateDataFlush();
}

function initBraze() {
  // GDPR: Check marketing consent before initializing.
  try {
    var gdprData = JSON.parse(localStorage.getItem('gdpr_consent'));
    if (!gdprData || !gdprData.preferences || !gdprData.preferences.marketing) {
      // console.log('[Braze] GDPR: marketing consent not given, skipping init');
      return;
    }
  } catch (e) {
    // console.log('[Braze] GDPR: marketing consent not given, skipping init');
    return;
  }

  var sdkKeyMeta = document.querySelector('meta[name="braze-sdk-key"]');
  var endpointMeta = document.querySelector('meta[name="braze-sdk-endpoint"]');
  if (!sdkKeyMeta || !endpointMeta) return;

  var sdkKey = sdkKeyMeta.getAttribute('content');
  var endpoint = endpointMeta.getAttribute('content');
  if (!sdkKey || !endpoint) return;

  // 1-2. Initialize SDK and register IAM handler (once only)
  if (!brazeSDKInitialized) {
    braze.initialize(sdkKey, {
      baseUrl: endpoint,
      allowUserSuppliedJavascript: true,
      sessionTimeoutInSeconds: 1
    });

    braze.automaticallyShowInAppMessages();
    brazeSDKInitialized = true;
  }

  // 3. Identify user and start session
  var userIdMeta = document.querySelector('meta[name="braze-user-id"]');
  if (userIdMeta) {
    var userId = userIdMeta.getAttribute('content');
    if (userId) {
      // console.log('[Braze] Identified user:', userId, '(external_id)');
      braze.changeUser(userId);
      startBrazeSession();
      return;
    }
  }

  // Anonymous visitor — fire web_page_ready to trigger Canvas popup
  if (!brazeAliasSet) {
    var aliasName = generateAliasName();
    // console.log('[Braze] Anonymous visitor, alias:', aliasName, '(eatokra_web_visitor)');
    braze.getUser().addAlias(aliasName, 'eatokra_web_visitor');
    brazeAliasSet = true;
  }
  startBrazeSession();
}

// turbolinks:load fires on initial page load AND every Turbolinks
// navigation (unlike DOMContentLoaded which only fires on full reloads)
document.addEventListener('turbolinks:load', initBraze);

// Also initialize when consent is granted mid-page
document.addEventListener('gdprConsentChanged', initBraze);
